import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { ErrorMessage, useFormikContext } from "formik";
import classNames from "classnames";
import { restHost } from "@default/js/config";
import { states } from "../states";
import DashedField from "./dashed-field";
import { formatWebformViolationMessage } from "../paragraph-form";

const EntityAutocompleteField = ({ item }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { visible, invisible, enabled, disabled, required } = states(
    item["#states"],
    values
  );
  const nameElement = item["#attributes"]?.["data-free-text-element"];
  const reference = values[item.id] || "";
  const [selection, setSelection] = useState(null);
  const label = nameElement
    ? values[nameElement] || ""
    : selection?.value === reference
    ? selection.label
    : "";
  const request = useRef(null);
  const timer = useRef(null);
  const [input, setInput] = useState("");
  const [failed, setFailed] = useState(false);
  const path = item["#autocomplete_path"];

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      request.current?.abort();
    },
    []
  );

  const loadOptions = useCallback(
    (search, callback) => {
      clearTimeout(timer.current);
      request.current?.abort();
      const query = search.trim();
      if (!path || query.length < 2 || query.length > 100) {
        callback([]);
        return;
      }
      timer.current = setTimeout(async () => {
        const controller = new AbortController();
        request.current = controller;
        try {
          const response = await fetch(
            `${restHost}${path}?q=${encodeURIComponent(query)}`,
            { signal: controller.signal }
          );
          if (!response.ok) throw new Error("Company lookup failed");
          const options = await response.json();
          if (!controller.signal.aborted) {
            setFailed(false);
            callback(
              options.map((option) => ({
                value: option.value.match(/\(([^()]*)\)"?$/)?.[1] || "",
                label: formatWebformViolationMessage(option.label),
              }))
            );
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            setFailed(true);
            callback([]);
          }
        }
      }, 250);
    },
    [path]
  );

  const choose = (option) => {
    setInput("");
    setSelection(option);
    setFieldValue(
      item.id,
      option && !option.__isNew__ ? option.value : "",
      false
    );
    if (nameElement) setFieldValue(nameElement, option?.label || "", false);
  };
  const changeInput = (next, { action }) => {
    if (action === "input-change") {
      setInput(next);
      setSelection(null); // Editing a selected name must discard its old company reference.
      setFieldValue(item.id, "", false);
      if (nameElement) setFieldValue(nameElement, next, false);
    }
  };
  const blur = () => {
    setInput("");
    setFieldTouched(item.id, true);
    if (nameElement) setFieldTouched(nameElement, true);
  };
  const Select = nameElement ? AsyncCreatableSelect : AsyncSelect;

  return (
    <div
      className={classNames("form-group cc-select", {
        hidden: invisible || !visible,
      })}
    >
      <label htmlFor={item.id} className="form-label">
        {item["#title"]}
        {visible && (item["#required"] || required || nameElement) && (
          <span className="required">*</span>
        )}
      </label>
      <DashedField>
        <Select
          inputId={item.id}
          instanceId={item.id}
          name={item.id}
          value={label ? { value: reference, label } : null}
          inputValue={input}
          loadOptions={loadOptions}
          defaultOptions={false}
          onChange={choose}
          onInputChange={changeInput}
          onBlur={blur}
          isClearable
          isDisabled={!enabled || disabled || item["#disabled"]}
          className="form-control"
          classNamePrefix="cc-select"
          placeholder={item["#placeholder"] || "Search"}
          formatCreateLabel={(name) => `Use “${name}”`}
          noOptionsMessage={() =>
            input.trim().length < 2
              ? "Type at least two characters"
              : "No matching company"
          }
        />
      </DashedField>
      <ErrorMessage component="span" name={item.id} className="error-message" />
      {nameElement && (
        <ErrorMessage
          component="span"
          name={nameElement}
          className="error-message"
        />
      )}
      {failed && (
        <small role="status">
          Company suggestions are temporarily unavailable. You can still enter a
          company name.
        </small>
      )}
      {item["#description"] && (
        <small className="form-description text-muted form-text">
          {item["#description"]}
        </small>
      )}
    </div>
  );
};

EntityAutocompleteField.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    "#states": PropTypes.object,
    "#attributes": PropTypes.object,
    "#autocomplete_path": PropTypes.string,
    "#title": PropTypes.string,
    "#required": PropTypes.bool,
    "#disabled": PropTypes.bool,
    "#placeholder": PropTypes.string,
    "#description": PropTypes.string,
  }).isRequired,
};

export default EntityAutocompleteField;
